/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import { Stack, styled, Tab, Tabs } from "@mui/material";

import MaterialSymbol from "../../../../components/MaterialSymbol";
import TestIds from "../../../../testIds";

import { useCampaignContext } from "./CampaignContext";
import DeliveryAddress from "./DeliveryAddress";
import PickupSite from "./PickupSite";
import TabHeader from "./TabHeader";
import { SaleAgreementPromo } from "./types";

interface SearchAddressTabProps {
  config: SaleAgreementPromo;
}

const SearchAddressTab = ({ config }: SearchAddressTabProps) => {
  return (
    <Stack
      direction="column"
      gap={2}
      data-testid={TestIds.feat.agreement.campaignModal.searchAddressTab}
    >
      <TabHeader
        title="Delivery address"
        caption="Please confirm your delivery address so we can include any freight costs"
      />
      <DeliveryMethods config={config} />
    </Stack>
  );
};

interface DeliveryMethodsProps {
  config: SaleAgreementPromo;
}

const DeliveryMethods = ({ config }: DeliveryMethodsProps) => {
  const { state, setState } = useCampaignContext();
  const { deliveryOptions } = config;
  const currentTabIndex = deliveryOptions.findIndex((d) => {
    return d.type === state.deliveryOption?.type;
  });

  function handleTabChange(value: number) {
    setState({
      deliveryOption: deliveryOptions[value],
    });
  }

  if (deliveryOptions.length === 1 && deliveryOptions[0].type === "Delivery") {
    return <DeliveryAddress />;
  }

  if (deliveryOptions.length === 1 && deliveryOptions[0].type === "Pickup") {
    return <PickupSite config={config} />;
  }

  return (
    <Stack direction="column">
      <StyledTabs
        value={currentTabIndex}
        onChange={(_, val) => handleTabChange(Number(val))}
        TabIndicatorProps={{ style: { display: "none" } }}
      >
        {deliveryOptions.map((deliveryOption, index) => {
          return (
            <StyledTab
              value={index}
              key={deliveryOption.type}
              icon={<DeliveryMethodIcon type={deliveryOption.type} />}
              iconPosition="start"
              label={deliveryOption.type}
            />
          );
        })}
      </StyledTabs>
      {deliveryOptions.map((deliveryOption, index) => {
        if (index !== currentTabIndex) {
          return null;
        }
        switch (deliveryOption.type) {
          case "Delivery":
            return <DeliveryAddress key="delivery" />;
          case "Pickup":
            return <PickupSite key="pickup" config={config} />;
          default:
            return null;
        }
      })}
    </Stack>
  );
};

interface DeliveryMethodIconProps {
  type: "Delivery" | "Pickup";
}

const DeliveryMethodIcon = ({ type }: DeliveryMethodIconProps) => {
  switch (type) {
    case "Delivery":
      return <MaterialSymbol sx={{ fontWeight: 400 }}>local_shipping</MaterialSymbol>;
    case "Pickup":
      return <MaterialSymbol sx={{ fontWeight: 400 }}>location_on</MaterialSymbol>;
    default:
      return null;
  }
};

const StyledTab = styled(Tab)(({ theme }) => ({
  ...theme.typography.body2,
  minHeight: 0,
  width: "200px",
  paddingTop: "12px",
  paddingBottom: "12px",
  paddingRight: "16px",
  paddingLeft: "16px",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  [theme.breakpoints.down("sm")]: {
    flex: "1 1",
  },
  backgroundColor: colors.primary25,
  "&:hover": {
    backgroundColor: colors.primary100,
  },
  "&.Mui-selected": {
    backgroundColor: colors.primary100,
    fontWeight: 700,
  },
  borderRadius: "50px",
  border: 0,
  color: colors.primary500,
}));

const StyledTabs = styled(Tabs)(() => ({
  minHeight: 0,
  marginBottom: "24px",
  "& .MuiTabs-flexContainer": {
    gap: "16px",
  },
}));

export default SearchAddressTab;
